// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { optionalBankIbanSchema } from '@client/banks/types/account';
import dayjs from 'dayjs';
import { z } from 'zod';

/** Debt product types supported in the upload form. */
export enum BankDebtProductType {
  Loan = 'loan',
  CreditLine = 'credit_line',
}

export const bankDebtProductTypeSchema = z.enum(BankDebtProductType);

export enum BankDebtAmortizationStatus {
  Paid = 'paid',
  Pending = 'pending',
}

export const bankDebtAmortizationStatusSchema = z.enum(BankDebtAmortizationStatus);

/** Installment row in the amortization schedule (optional on the debt). */
export const bankDebtAmortizationSchema = z.object({
  installmentDate: z.string().min(1),
  status: bankDebtAmortizationStatusSchema.optional(),
  amortized: z.number().optional(),
  interest: z.number().optional(),
  total: z.number().optional(),
  pendingCapital: z.number().optional(),
});

export type BankDebtAmortization = z.infer<typeof bankDebtAmortizationSchema>;

/**
 * Loan or credit line with a single outstanding-amount point.
 *
 * @remarks
 * UI form row (camelCase). Maps 1:1 to `AdvisoryAccountsCitecDebt` via
 * `buildBulkUploadAccountData` in
 * `@client/banks/utils/build-bulk-upload-account-data`.
 * @example
 *
 * ```ts
 * const debt = bankDebtSchema.parse({
 *   iban: 'ES9121000418450200051332',
 *   productType: BankDebtProductType.Loan,
 *   name: 'Mortgage',
 *   currency: 'EUR',
 *   pendingAmount: 180000,
 *   snapshotDate: '2024-06-01',
 * });
 * ```
 */
export const bankDebtSchema = z.object({
  iban: optionalBankIbanSchema,
  productType: bankDebtProductTypeSchema,
  name: z.string().trim().min(1),
  currency: z.string(),
  pendingAmount: z.number(),
  snapshotDate: z.string().check(({ value, issues }) => {
    const parsed = dayjs(value);

    if (parsed.isValid() && parsed.isAfter(dayjs(), 'day')) {
      issues.push({
        code: 'custom',
        input: value,
        message: 'Date cannot be in the future',
      });
    }
  }),
  contractNumber: z.string().optional(),
  grantedCapital: z.number().optional(),
  interestRate: z.number().optional(),
  limitAmount: z.number().optional(),
  constitutionDate: z.string().optional(),
  expirationDate: z.string().optional(),
  nextInstallment: z.number().optional(),
  pendingReceipts: z.number().optional(),
  available: z.number().optional(),
  disposed: z.number().optional(),
  currentBalance: z.number().optional(),
  nextPaymentDate: z.string().optional(),
  amortizations: z.array(bankDebtAmortizationSchema).optional(),
});

export type BankDebt = z.infer<typeof bankDebtSchema>;
